import React, { Dispatch, SetStateAction, useEffect } from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';

import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup from '@atlaskit/popup';
import Select, { OptionsType } from '@atlaskit/select';

import { useIntl } from 'src/hooks/intl';
import { changeDiffSort } from 'src/redux/pull-request/actions';
import { getIsStickyHeaderActive } from 'src/redux/pull-request/selectors';
import { useMatch } from 'src/router/hooks';
import { getIsSiteMessageActive } from 'src/selectors/global-selectors';
import { BucketState } from 'src/types/state';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { buttonAppearance, buttonSpacing, PR_PATH } from './constants';
import {
  FilterOrSortSection,
  getSortBySelectStyles,
} from './diff-comment-filters/styles';

type DiffSortOption = {
  label: string;
  value: 'fileTree' | 'latestComments';
};

const { fileTreeLabel, commentsLabel, sortByLabel, sortByOption } =
  defineMessages({
    fileTreeLabel: {
      id: 'frontbucket.pullRequest.sorting.fileTree',
      description:
        'Option text for pull requests to sort files as they appear in the file tree',
      defaultMessage: 'File tree',
    },
    commentsLabel: {
      id: 'frontbucket.pullRequest.sorting.recentComments',
      description:
        'Option text for pull requests to sort files according to the most recent comment',
      defaultMessage: 'Recent comments',
    },
    sortByLabel: {
      id: 'frontbucket.pullRequest.sorting.label',
      description:
        'Label text for pull requests to sort files by a certain option',
      defaultMessage: 'Sort by',
    },
    sortByOption: {
      id: 'frontbucket.pullRequest.sorting.optionLabel',
      description:
        'Label text for pull requests to sort files by a certain option',
      defaultMessage: 'Sort by {option}',
    },
  });

export type DiffSortProps = {
  isDiffSortOpen: boolean;
  setIsDiffSortOpen: Dispatch<SetStateAction<boolean>>;
  isStickyHeader?: boolean;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const DiffSort: React.FC<DiffSortProps> = ({
  isDiffSortOpen,
  setIsDiffSortOpen,
  isStickyHeader = false,
}) => {
  const intl = useIntl();
  const isSiteBannerOpen = useSelector(getIsSiteMessageActive);
  const styles = getSortBySelectStyles(isSiteBannerOpen);

  const { path } = useMatch();
  const isPrPage = path === PR_PATH;
  const tabIndex = isStickyHeader ? -1 : 0;

  // If this is not being rendered in the sticky header,
  // we need to close the selector when the sticky header is activated.
  // This prevents the scrolling bug found in COREX-8483.
  const isStickyHeaderActive = useSelector(getIsStickyHeaderActive);
  useEffect(() => {
    if (isStickyHeaderActive && !isStickyHeader) {
      setIsDiffSortOpen(false);
    }
  }, [isStickyHeaderActive, isStickyHeader, setIsDiffSortOpen]);

  const diffSort = useSelector(
    (state: BucketState) => state.repository.pullRequest.diffSort
  );
  const dispatch = useDispatch();
  const onChange = (option: DiffSortOption) => {
    dispatch(changeDiffSort(option.value));

    // trigger analytics event
    publishUiEvent({
      action: 'updated',
      actionSubject: 'dropdown',
      actionSubjectId: 'fileSortOrderDropdown',
      source: 'pullRequestScreen',
      attributes: {
        fileSortOrder: option.value,
      },
    });
  };

  const onClick = (option: DiffSortOption) => {
    onChange(option);
    setIsDiffSortOpen(false);
  };

  const options: OptionsType<DiffSortOption> = [
    {
      label: intl.formatMessage(fileTreeLabel),
      value: 'fileTree',
    },
    {
      label: intl.formatMessage(commentsLabel),
      value: 'latestComments',
    },
  ];

  const popupOptions = () => {
    return (
      <MenuGroup>
        <Section>
          <ButtonItem
            onClick={() => {
              onClick(options[0]);
            }}
            isSelected={diffSort === options[0].value}
          >
            <FormattedMessage {...fileTreeLabel} />
          </ButtonItem>
          <ButtonItem
            onClick={() => {
              onClick(options[1]);
            }}
            isSelected={diffSort === options[1].value}
          >
            <FormattedMessage {...commentsLabel} />
          </ButtonItem>
        </Section>
      </MenuGroup>
    );
  };

  return isPrPage ? (
    <Popup
      id="sort-by-popup"
      isOpen={isDiffSortOpen}
      onClose={() => setIsDiffSortOpen(false)}
      placement="bottom-start"
      content={popupOptions}
      shouldFlip={false}
      trigger={triggerProps => (
        <Button
          {...triggerProps}
          appearance={buttonAppearance(isStickyHeader)}
          spacing={buttonSpacing(isStickyHeader)}
          onClick={() => setIsDiffSortOpen(!isDiffSortOpen)}
          iconAfter={<ChevronDownIcon label="toggle-sort-popup" />}
          isSelected={isDiffSortOpen}
          tabIndex={tabIndex}
        >
          <FormattedMessage
            {...sortByOption}
            values={{
              option: options.find(o => o.value === diffSort)?.label ?? '',
            }}
          />
        </Button>
      )}
    />
  ) : (
    <FilterOrSortSection>
      <div>
        <FormattedMessage {...sortByLabel} />
      </div>
      <Select<DiffSortOption>
        isSearchable={false}
        options={options}
        onChange={onChange}
        styles={styles}
        value={options.find(o => o.value === diffSort)}
        onMenuOpen={() => setIsDiffSortOpen(true)}
        onMenuClose={() => setIsDiffSortOpen(false)}
      />
    </FilterOrSortSection>
  );
};
